import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type { ScheduleSeasonResponse, SeasonApiRecord, SeasonsListResponse } from "../../types/gamePass";
import { getPrivateInstance } from "../_privateInstance";
import { unwrapGamePassEntity } from "./responseMappers";

const buildScheduleSeasonUrl = (seasonId: string): string =>
  API_ROUTES.SCHEDULE_SEASON.replace("{season_id}", seasonId);

const postScheduleSeason = async (seasonId: string): Promise<SeasonApiRecord> => {
  const response = await getPrivateInstance().post<ScheduleSeasonResponse>(
    buildScheduleSeasonUrl(seasonId),
    {},
  );
  return unwrapGamePassEntity(response.data, "season");
};

export const useScheduleSeason = (): UseMutationResult<
  SeasonApiRecord,
  Error,
  string
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["game-pass", "seasons", "schedule"],
    mutationFn: postScheduleSeason,
    onSuccess: (season) => {
      queryClient.setQueriesData<SeasonsListResponse>(
        { queryKey: ["game-pass", "seasons", "list"] },
        (current) => {
          if (!current) return current;
          return {
            ...current,
            seasons: current.seasons.map((record) =>
              (record.id ?? record.season_id) === (season.id ?? season.season_id)
                ? { ...record, ...season }
                : record,
            ),
          };
        },
      );
      queryClient.invalidateQueries({
        queryKey: ["game-pass", "seasons", "list"],
      });
    },
  });
};
